import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Plus, ShieldCheck, Camera, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/Primitives';

// === ОНОВЛЕНІ ДАНІ (Офіційний стиль / Premium) ===

const heroData = {
  title: 'ПРОФЕСІЙНЕ УПРАВЛІННЯ',
  subtitle: 'ТА РОЗВИТОК ПРОФІЛЮ',
  highlight: 'НА МІЖНАРОДНИХ ПЛАТФОРМАХ',
  ending: '',
  description:
    'BMGroup бере на себе повний цикл роботи з профілем: реєстрацію, верифікацію, контент і ведення. Ви працюєте дистанційно, зберігаючи приватність та контроль, отримуючи стабільний дохід.',
  cta: 'СТАТИ ЧАСТИНОЮ BMGroup'
};

const aboutData = {
  title: 'BMGroup — це система',
  subtitle: 'де профіль працює стабільно',
  description:
    'BMGroup спеціалізується на професійному управлінні профілями для міжнародних платформ закритого доступу. Ми вибудовуємо процес так, щоб кожен профіль розвивався прогнозовано, без хаосу й ризиків. Чітка структура, контроль якості та довготривала співпраця — основа нашого підходу.',
  stats: [
    { target: 1500, suffix: '+', label: 'активних запусків', note: 'за 3 роки роботи' },
    { target: 8, suffix: '+', label: 'міжнародних платформ', note: 'перевірені та активні' },
    { target: 3, suffix: '+', label: 'роки системної роботи', note: 'стабільний досвід' },
    { target: 100, suffix: '%', label: 'прозора звітність', note: 'без прихованих умов' }
  ]
};

// === АНІМОВАНИЙ ЛІЧИЛЬНИК ===
const useCountUp = (target, duration = 1800) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    const steps = 60;
    const stepTime = duration / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += 1;
      const progress = current / steps;
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (current >= steps) clearInterval(timer);
    }, stepTime);
    return () => clearInterval(timer);
  }, [started, target, duration]);

  const formatted = count >= 1000
    ? `${Math.floor(count / 1000)} ${String(count % 1000).padStart(3, '0')}`
    : String(count);

  return { ref, formatted };
};


const benefitsData = [
  {
    title: 'КОНТРОЛЬ ДОСТУПУ ТА ДАНИХ',
    description:
      'Доступ до профілю та контенту здійснюється винятково в межах погодженого формату. Ми дотримуємося принципу мінімального ризику та захисту персональних даних.',
    icon: 'shield'
  },
  {
    title: 'СТРУКТУРОВАНИЙ КОНТЕНТ',
    description:
      'Ми не просимо хаотичні матеріали. Ви отримуєте чітке розуміння, що і коли потрібно оновлювати для стабільного розвитку профілю.',
    icon: 'camera'
  },
  {
    title: 'ПРОЗОРИЙ ФІНАНСОВИЙ ПРОЦЕС',
    description:
      'Усі нарахування базуються на відкритій аналітиці. Зручні формати виплат без прихованих умов.',
    icon: 'trending'
  }
];


const iconMap = {
  shield: ShieldCheck,
  camera: Camera,
  trending: TrendingUp
};

// === ВНУТРІШНІ КОМПОНЕНТИ ===

const StatCard = ({ stat }) => {
  const { ref, formatted } = useCountUp(stat.target);
  return (
    <div
      ref={ref}
      className="bg-white/5 backdrop-blur-sm border border-white/10 p-5 md:p-8 rounded-xl text-center hover:bg-white/10 transition-all duration-300 hover:scale-105 group"
    >
      <div className="text-4xl md:text-5xl font-bold text-emerald-500 mb-1 group-hover:scale-110 transition-transform">
        {formatted}{stat.suffix}
      </div>
      <div className="text-white text-sm font-medium mb-1">{stat.label}</div>
      {stat.note && <div className="text-gray-500 text-xs">{stat.note}</div>}
    </div>
  );
};

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-20 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

      <div className="container mx-auto px-4 py-20 md:py-32 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="flex items-center space-x-2">
              <Plus className="text-emerald-500" size={32} />
              <div className="h-px w-12 bg-emerald-500" />
            </div>

            <div className="space-y-4">
              <h1 className="text-3xl md:text-6xl font-bold leading-tight">
                <span className="text-white block">{heroData.title}</span>
                <span className="text-white block">{heroData.subtitle}</span>
                <span className="text-emerald-500 block italic">{heroData.highlight}</span>
              </h1>
            </div>

            <p className="text-gray-400 text-lg max-w-md">{heroData.description}</p>

            <div className="flex items-center space-x-4">
              <a
                href="https://t.me/bmg_managerrrr"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  size="lg"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium px-8 py-6 text-lg transition-all duration-300 hover:scale-105 group"
                >
                  {heroData.cta}
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Button>
              </a>
            </div>
          </div>

          {/* Right Content - Info Cards */}
          <div className="space-y-6">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-5 md:p-8 rounded-2xl hover:bg-white/10 transition-all duration-300 hover:scale-105 group">
				<h3 className="text-white text-xl font-bold mb-3">
				  ФОРМАЛІЗОВАНА СПІВПРАЦЯ
				</h3>
				<p className="text-gray-400">
				  Робота в межах чітких домовленостей, прозорих умов та заздалегідь узгоджених правил.
				</p>

            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-5 md:p-8 rounded-2xl hover:bg-white/10 transition-all duration-300 hover:scale-105 group">
				<h3 className="text-white text-xl font-bold mb-3">
				  ДИСТАНЦІЙНИЙ ФОРМАТ
				</h3>
				<p className="text-gray-400">
				  Весь процес — онлайн: від запуску профілю до щоденної підтримки й управління.
				</p>

            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-emerald-500/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-emerald-500 rounded-full" />
        </div>
      </div>
    </section>
  );
};

const About = () => {
  return (
    <section className="py-12 md:py-24 bg-zinc-950 relative overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.02)_1px,transparent_1px)] bg-[size:30px_30px]" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">{aboutData.title}</h2>
            <p className="text-xl text-emerald-500 font-medium">{aboutData.subtitle}</p>
          </div>

          {/* Description */}
          <div className="text-center mb-16">
            <p className="text-gray-400 text-lg max-w-3xl mx-auto leading-relaxed">{aboutData.description}</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {aboutData.stats.map((stat, index) => (
              <StatCard key={index} stat={stat} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// === ГОЛОВНА СТОРІНКА ===

const Home = () => {
  return (
    <div className="bg-black">
      <Hero />
      <About />

      {/* Benefits Section */}
      <section className="py-12 md:py-24 bg-black relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {benefitsData.map((benefit, index) => {
                const Icon = iconMap[benefit.icon];
                return (
                  <div
                    key={index}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 p-5 md:p-8 rounded-2xl hover:bg-white/10 transition-all duration-300 hover:scale-105 group"
                  >
                    <div className="mb-6">
                      <div className="w-14 h-14 bg-emerald-500/10 rounded-xl flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                        <Icon className="text-emerald-500" size={28} />
                      </div>
                    </div>
                    <h3 className="text-white text-xl font-bold mb-4">{benefit.title}</h3>
                    <p className="text-gray-400 leading-relaxed">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-24 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
			<h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
			  Співпраця без зайвого шуму
			</h2>
			<p className="text-gray-400 text-xl mb-8">
			  Ми цінуємо приватність, дисципліну та довготривалі результати. 
			  Якщо вам близький такий підхід — будемо раді знайомству.
			</p>

            <Link to="/contact">
              <Button
                size="lg"
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium px-10 py-6 text-lg transition-all duration-300 hover:scale-105"
              >
                ПОДАТИ ЗАЯВКУ
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
